import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Crown, Mail, Lock, User, ArrowRight, Building2, Globe, Shield } from 'lucide-react';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle, user } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'citizen'
  });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) {
      const roleRoutes = {
        citizen: '/citizen',
        institution: '/institution',
        government: '/government',
        international: '/international',
        admin: '/government'
      };
      navigate(roleRoutes[user.role] || '/citizen');
    }
  }, [user, navigate]);

  const roleOptions = [
    { value: 'citizen', label: 'Citizen', icon: <User className="h-4 w-4" />, description: 'Access CBDC wallet and payments' },
    { value: 'institution', label: 'Financial Institution', icon: <Building2 className="h-4 w-4" />, description: 'Banking and compliance services' },
    { value: 'government', label: 'Government Agency', icon: <Shield className="h-4 w-4" />, description: 'Treasury and sovereign operations' },
    { value: 'international', label: 'International Partner', icon: <Globe className="h-4 w-4" />, description: 'Cross-border settlements' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const result = await register(formData.email, formData.password, formData.name, formData.role);
      toast.success('Account created successfully!');
      const roleRoutes = {
        citizen: '/citizen',
        institution: '/institution',
        government: '/government',
        international: '/international',
        admin: '/government'
      };
      navigate(roleRoutes[result.role] || '/citizen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFD700' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-12 h-12 gold-gradient rounded-sm flex items-center justify-center">
              <Crown className="h-7 w-7 text-black" />
            </div>
            <span className="font-serif text-2xl font-semibold text-white">Royal CBDC</span>
          </Link>
          
          <div>
            <h1 className="font-serif text-4xl xl:text-5xl font-light text-white leading-tight mb-6">
              Join the Digital<br />
              <span className="font-semibold text-gold">Financial Revolution</span>
            </h1>
            <p className="text-white/60 text-lg max-w-md">
              Create your account to access digital currency services, instant payments, and comprehensive financial tools.
            </p>
          </div>

          <p className="text-white/40 text-sm">
            © 2024 Royal CBDC
          </p>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto bg-white">
        <div className="w-full max-w-md py-8">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="w-10 h-10 gold-gradient rounded-sm flex items-center justify-center">
                <Crown className="h-6 w-6 text-black" />
              </div>
              <span className="font-serif text-xl font-semibold text-black">Royal CBDC</span>
            </Link>
          </div>

          <Card className="border-black/10 shadow-none lg:shadow-sm">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="font-serif text-2xl md:text-3xl font-normal text-black">Create Account</CardTitle>
              <CardDescription className="text-black/60">
                Register to access digital financial services
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-black">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black/40" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 border-black/20 focus:border-gold"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-black">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black/40" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 border-black/20 focus:border-gold"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-black">Account Type</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="border-black/20" data-testid="register-role-select">
                      <SelectValue placeholder="Select account type" />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            {option.icon}
                            <span>{option.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-black/50">
                    {roleOptions.find(r => r.value === formData.role)?.description}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-black">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black/40" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Create a password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 border-black/20 focus:border-gold"
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-black">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black/40" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="pl-10 border-black/20 focus:border-gold"
                      required
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full py-6 text-sm uppercase tracking-widest bg-black hover:bg-black/90 text-white"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-black/10"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-black/40">Or continue with</span>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="w-full py-6 border-black/20 hover:bg-black/5 text-black"
                onClick={loginWithGoogle}
                data-testid="register-google-btn"
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <p className="mt-6 text-center text-sm text-black/60">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-gold hover:text-gold/80" data-testid="register-login-link">
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
